import React, { useState, useEffect, useCallback } from 'react'
import { Checkbox, Input, Badge, Spin, Empty, Dropdown, message as antMessage } from 'antd'
import { UserOutlined, SearchOutlined, CommentOutlined, MessageOutlined, ReloadOutlined, LoginOutlined } from '@ant-design/icons'
import api from '../../api'

const PLATFORM_COLORS = {
  douyin: '#ff4d4f', kuaishou: '#fa8c16', xiaohongshu: '#eb2f96',
  weibo: '#1890ff', bilibili: '#13c2c2', shipinhao: '#52c41a', jinritoutiao: '#722ed1'
}

const PLATFORM_NAMES = {
  douyin: '抖音', kuaishou: '快手', xiaohongshu: '小红书',
  weibo: '微博', bilibili: 'B站', shipinhao: '视频号', jinritoutiao: '头条'
}

function AccountInteractionList({ onSelect, selectedIds, onToggleSelect }) {
  const [accounts, setAccounts] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  const loadAccounts = useCallback(async () => {
    setLoading(true)
    const list = await api.getAccounts()
    const sorted = (list || [])
      .filter(a => !a.is_login_session)
      .sort((a, b) => {
        if (a.status === 'online' && b.status !== 'online') return -1
        if (b.status === 'online' && a.status !== 'online') return 1
        return 0
      })
    setAccounts(sorted)
    setLoading(false)
  }, [])

  useEffect(() => { loadAccounts() }, [loadAccounts])
  useEffect(() => {
    const unsub1 = api.on('accounts-refreshed', loadAccounts)
    const unsub2 = api.on('account-updated', (updated) => {
      if (!updated?.account_id) return
      setAccounts(prev => {
        const exists = prev.some(a => a.account_id === updated.account_id)
        if (!exists) return prev
        const merged = prev.map(a => a.account_id === updated.account_id ? { ...a, ...updated } : a)
        merged.sort((a, b) => {
          if (a.status === 'online' && b.status !== 'online') return -1
          if (b.status === 'online' && a.status !== 'online') return 1
          return 0
        })
        return merged
      })
    })
    return () => { unsub1(); unsub2() }
  }, [loadAccounts])

  const handleReLogin = async (acc) => {
    try {
      const res = await api.reLogin(acc.account_id)
      if (res && res.success) {
        antMessage.success('正在重新登录...')
        loadAccounts()
      } else {
        antMessage.error(res?.message || '重新登录失败')
      }
    } catch {
      antMessage.error('重新登录失败')
    }
  }

  const filtered = accounts.filter(a => {
    if (!search) return true
    const t = search.toLowerCase()
    return (a.nickname || '').toLowerCase().includes(t) ||
      (a.remark || '').toLowerCase().includes(t) ||
      (PLATFORM_NAMES[a.platform] || '').includes(search)
  })

  const allSelected = filtered.length > 0 && filtered.every(a => selectedIds.includes(a.account_id))
  const handleSelectAll = () => {
    if (allSelected) {
      filtered.forEach(a => onToggleSelect(a.account_id))
    } else {
      filtered.forEach(a => {
        if (!selectedIds.includes(a.account_id)) onToggleSelect(a.account_id)
      })
    }
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#fafafa', borderRight: '1px solid #e8e8e8' }}>
      <div style={{ padding: 12, borderBottom: '1px solid #e8e8e8' }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <Input
            prefix={<SearchOutlined />}
            placeholder="搜索账号..."
            allowClear
            size="small"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <ReloadOutlined onClick={loadAccounts} style={{ cursor: 'pointer', color: '#999', fontSize: 14 }} />
        </div>
      </div>

      {filtered.length > 1 && (
        <div style={{ padding: '4px 16px', borderBottom: '1px solid #f0f0f0', fontSize: 12 }}>
          <Checkbox checked={allSelected} onChange={handleSelectAll} style={{ fontSize: 12 }}>
            全选 ({filtered.length})
          </Checkbox>
        </div>
      )}

      <div style={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : filtered.length === 0 ? (
          <Empty description="暂无账号" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ marginTop: 40 }} />
        ) : (
          filtered.map((acc, idx) => (
            <AccountItem
              key={acc.account_id}
              acc={acc}
              isSelected={selectedIds.includes(acc.account_id)}
              selectedIds={selectedIds}
              onSelect={onSelect}
              onToggleSelect={onToggleSelect}
              onReLogin={handleReLogin}
              isLast={idx === filtered.length - 1}
            />
          ))
        )}
      </div>
    </div>
  )
}

function AccountItem({ acc, isSelected, selectedIds, onSelect, onToggleSelect, onReLogin, isLast }) {
  const displayName = acc.remark || acc.nickname || PLATFORM_NAMES[acc.platform] || '未命名'
  const isSingleSelected = selectedIds.length === 1 && selectedIds[0] === acc.account_id
  const isOnline = acc.status === 'online'

  const contextMenu = {
    items: [
      {
        key: 're-login',
        icon: <LoginOutlined />,
        label: '重新登录',
        onClick: () => onReLogin(acc)
      }
    ]
  }

  const content = (
    <div
      onClick={(e) => {
        if (e.target.type === 'checkbox') return
        onSelect(acc)
      }}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
        cursor: 'pointer',
        background: isSingleSelected ? '#e6f4ff' : 'transparent',
        borderLeft: isSingleSelected ? '3px solid #1677ff' : '3px solid transparent',
        borderBottom: isLast ? 'none' : '1px solid #f0f0f0',
        opacity: isOnline ? 1 : 0.55,
        transition: 'background 0.15s'
      }}
      onMouseEnter={e => { if (!isSingleSelected) e.currentTarget.style.background = '#f5f5f5' }}
      onMouseLeave={e => { if (!isSingleSelected) e.currentTarget.style.background = 'transparent' }}
    >
      <Checkbox
        checked={isSelected}
        onChange={() => onToggleSelect(acc.account_id)}
        onClick={e => e.stopPropagation()}
        style={{ flexShrink: 0 }}
      />

      <div style={{ position: 'relative', flexShrink: 0 }}>
        <div style={{
          width: 32, height: 32, borderRadius: '50%', overflow: 'hidden',
          border: `2px solid ${isOnline ? '#52c41a' : '#d9d9d9'}`,
          background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          {acc.avatar_url ? (
            <img src={acc.avatar_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', filter: isOnline ? 'none' : 'grayscale(100%)' }}
              onError={e => { e.target.style.display = 'none' }} />
          ) : (
            <UserOutlined style={{ color: isOnline ? '#fff' : '#bbb', fontSize: 14 }} />
          )}
        </div>
        <div style={{
          position: 'absolute', bottom: -1, right: -1,
          width: 14, height: 14, borderRadius: '50%',
          background: PLATFORM_COLORS[acc.platform] || '#999',
          border: '2px solid #fff', fontSize: 7,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 'bold'
        }}>
          {(PLATFORM_NAMES[acc.platform] || '?')[0]}
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayName}
        </div>
        <div style={{ fontSize: 11, color: '#999', marginTop: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
          <span style={{
            width: 6, height: 6, borderRadius: '50%',
            background: isOnline ? '#52c41a' : '#d9d9d9',
            display: 'inline-block'
          }} />
          {PLATFORM_NAMES[acc.platform]}
          {!isOnline && <span style={{ color: '#faad14' }}>· 离线</span>}
        </div>
      </div>
    </div>
  )

  if (!isOnline) {
    return (
      <Dropdown menu={contextMenu} trigger={['contextMenu']}>
        {content}
      </Dropdown>
    )
  }

  return content
}

export default AccountInteractionList
